import { canWriteDeck } from "@/lib/deckSession";
import { createEmptyDeck } from "@/lib/deckFactory";
import {
  createDeck,
  getNuggets,
  isPersistenceAvailable,
  loadPersistedState,
  noteDeckRev,
  putDeck,
} from "@/lib/idb";
import { materializeFrom } from "@/lib/nugget";
import type { DeckDocument } from "@/types/document";

import { store } from "./index";
import { hydrated as documentHydrated } from "./documentSlice";
import { hydrated as chartHydrated } from "./chartSlice";
import { hydrated as diagramHydrated } from "./diagramSlice";
import { hydrated as ganttHydrated } from "./ganttSlice";
import { hydrated as griddHydrated } from "./griddSlice";
import { draftAbandoned } from "./styleDraftSlice";
import { migrateDeck } from "./migrate";

// Reads one deck out of IndexedDB and hydrates the store from it (TODO §4.4 / §12.2.1).
//
// This used to run exactly once at boot and take `decks[0]` — the single-deck assumption.
// It is now **deck-scoped and re-runnable**: the open deck is whatever `/d/:deckId` says
// (§12.1), and the same call re-hydrates a read-only viewer when the writing page saves
// (§12.5.3).
//
// Two things make re-running safe:
//   • both `hydrated` reducers REPLACE their state wholesale rather than merging, so one
//     deck's grid bodies can never bleed into another (§12's problem 3);
//   • `styleDraft` is dropped first — it is session-only preview state belonging to the
//     deck we are leaving, and pushing it at the next deck's embeds would restyle them
//     from a sheet that is not theirs (§12.2.3).
//
// Returns false when the deck does not exist, which the route turns into "deck not found"
// (§12.1.2) rather than a silent empty editor.
export async function hydrateStore(deckId: string): Promise<boolean> {
  const { deck, griddDocs, diagramDocs, ganttDocs, chartDocs } =
    await loadPersistedState(deckId);
  if (!deck) {
    // Nothing came back. Either the deck genuinely is not there — the caller's cue to show
    // "deck not found" — or there is no storage at all (Safari private browsing, blocked
    // IndexedDB), in which case NOTHING would ever be found and the studio would be a
    // permanent 404. The pre-§12 app degraded to an in-memory deck here (§4.5), so it still
    // does: seed one under the routed id and let the session run entirely in memory.
    if (await isPersistenceAvailable()) return false;
    store.dispatch(draftAbandoned());
    store.dispatch(documentHydrated({ ...createEmptyDeck(), id: deckId }));
    store.dispatch(griddHydrated({}));
    store.dispatch(diagramHydrated({}));
    store.dispatch(ganttHydrated({}));
    store.dispatch(chartHydrated({}));
    return true;
  }

  // Migrate an older persisted schema up before it enters the store (TODO §2.2).
  // migrateDeck returns the same reference when already current, so an inequality
  // means we upgraded and should write the new shape back to IndexedDB — hydration
  // itself is deliberately excluded from the persistence listener.
  const migrated = await reconcileNuggetLinks(migrateDeck(deck));
  // The base this page's next write is measured against (§12.5.4). Set before dispatching,
  // so a write triggered by the first render can never race ahead of it.
  noteDeckRev(deck.id, deck.meta?.rev ?? 0);

  store.dispatch(draftAbandoned());
  store.dispatch(documentHydrated(migrated));
  store.dispatch(griddHydrated(griddDocs));
  store.dispatch(diagramHydrated(diagramDocs));
  store.dispatch(ganttHydrated(ganttDocs));
  store.dispatch(chartHydrated(chartDocs));

  // A schema upgrade is still a write, so it obeys the same single-writer rule as any other
  // (§12.5.1). The page that owns the deck will persist the upgrade; a viewer just uses it.
  if (migrated !== deck && canWriteDeck(deck.id)) await putDeck(migrated);
  return true;
}

// §17.2.5 Bring every linked slide into step with its nugget, on the way in.
//
// Two cases, and the second is the one worth reading twice:
//
//   • `nugget.rev > link.rev` — the nugget moved on while this deck was closed (another deck
//     edited it, or this one was a read-only viewer at the time). Replace the mirrored fields
//     from the nugget and match the rev. Everything §17.2.1 does not mirror — the slide's id,
//     whether it is archived, its pyramid level — stays exactly as this deck had it.
//
//   • **The nugget is GONE.** Keep the slide precisely as it is and drop the link. A nugget
//     deleted on the library shelf must never blank a slide in a deck that used it — which is
//     the whole reason §17.1.6 stores the materialized `SlideData` in the deck rather than a
//     pointer. The slide simply stops being shared, and nobody loses a page of work because
//     someone else tidied the shelf.
//
// Returns the deck BY REFERENCE when nothing changed, so `migrateDeck`'s identity contract
// survives this call: bootstrap uses `migrated !== deck` to decide whether to write back, and
// a reconcile that rebuilt the object every time would rewrite every deck on every open.
async function reconcileNuggetLinks(deck: DeckDocument): Promise<DeckDocument> {
  const linkedIds = deck.slides.flatMap((s) =>
    s.link ? [s.link.nuggetId] : [],
  );
  if (linkedIds.length === 0) return deck;
  const nuggets = await getNuggets(linkedIds);
  let changed = false;
  const slides = deck.slides.map((slide) => {
    const link = slide.link;
    if (!link) return slide;
    const nugget = nuggets[link.nuggetId];
    if (!nugget) {
      changed = true;
      const unlinked = { ...slide };
      delete unlinked.link;
      return unlinked;
    }
    if (nugget.rev <= link.rev) return slide;
    changed = true;
    return {
      ...slide,
      ...materializeFrom(nugget),
      link: { nuggetId: link.nuggetId, rev: nugget.rev },
    };
  });
  return changed ? { ...deck, slides } : deck;
}

// Mint and persist a brand-new deck, returning its id for the caller to navigate to
// (§12.4.3). Deck creation is no longer a side-effect of booting into an empty database —
// it is an explicit act, from the documents home's "New document" or from `/` finding
// nothing to open.
export async function createNewDeck(title?: string): Promise<string> {
  const deck = createEmptyDeck(title);
  await createDeck(deck);
  return deck.id;
}
